<script setup>
import { ref, onMounted } from 'vue'
import { Switch } from '@headlessui/vue'
import { Head, Link, useForm } from '@inertiajs/vue3'
import GoogleAutoComplete from '../../../Components/GoogleAutoComplete.vue'
import Checkbox from 'primevue/checkbox';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import InputText from 'primevue/inputtext';

const props = defineProps({ dealer: Object, categories: Array, brands: Array })

const address = ref(null)
const invoiceAddress = ref(null)
const form = useForm({
  company_name: props.dealer.company_name,
  email: props.dealer.email,
  website: props.dealer.website,
  description: props.dealer.description,
  address: props.dealer.address,
  invoice_address: props.dealer.invoice_address,
  first_name: props.dealer.first_name,
  infix: props.dealer.infix,
  last_name: props.dealer.last_name,
  whatsapp_number: props.dealer.whatsapp_number,
  phone: props.dealer.phone_number,
  chamber_of_commerce_number: props.dealer.chamber_of_commerce_number,
  vat_number: props.dealer.vat_number,
  brands: props.dealer.brands.map(brand => brand.id),
  categories: props.dealer.categories.map(category => category.id),
  notifications: {
    email: {
      new_offer: ref(props.dealer.notification_settings.email.new_offer),
      personal_offer: ref(props.dealer.notification_settings.email.personal_offer),
    }
  }
})

function getAndFormatAddressData(addressData, placeResultData, id) {
  form.address = JSON.stringify(addressData);
}

function getAndFormatInvoiceAddressData(addressData, placeResultData, id) {
  form.invoice_address = JSON.stringify(addressData);
}

function submit() {
  form.post('/portal/dealer/' + props.dealer.id + '/edit', {
    onSuccess: () => {
      //
    },
    onError: () => {
      //
    }
  });
}

onMounted(() => {
  const latlng = { lat: form.address.latitude, lng: form.address.longitude };
  address.value.updateCoordinates(latlng);

  if (form.invoice_address === undefined) {
    return;
  }
  const invoiceLatlng = { lat: form.invoice_address.latitude, lng: form.invoice_address.longitude };
  invoiceAddress.value.updateCoordinates(invoiceLatlng);
})
</script>
<template>
  <Head title="Dealer aanpassen | Mijnautosnelweg" />
  <form class="px-4 sm:px-6 py-6 space-y-12" @submit.prevent="submit">
    <div class="space-y-12">
      <div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Dealer aanpassen</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Vul hier alle informatie in om de dealer zo volledig mogelijk
            aan te maken!</p>
        </div>

        <div class="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
          <div class="col-span-full">
            <label for="company_name" class="block text-sm font-medium leading-6 text-gray-900">Bedrijfsnaam</label>
            <div class="mt-2">
              <div
                class="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-brightorange">
                <input type="text" name="company_name" id="company_name" autocomplete="company_name"
                  v-model="form.company_name"
                  class="block flex-1 border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  placeholder="Bedrijfsnaam" />
              </div>
              <div class="text-red-500" v-if="form.errors.company_name">{{ form.errors.company_name }}</div>
            </div>
          </div>
          <div class="sm:col-span-3">
            <label for="email" class="block text-sm font-medium leading-6 text-gray-900">E-mail</label>
            <div class="mt-2">
              <div
                class="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-brightorange">
                <input type="email" name="email" id="email" autocomplete="email" v-model="form.email"
                  class="input-style"
                  placeholder="E-mail" />
              </div>

              <div class="text-red-500" v-if="form.errors.email">{{ form.errors.email }}</div>
            </div>
          </div>

          <div class="sm:col-span-3">
            <label for="username" class="block text-sm font-medium leading-6 text-gray-900">Website</label>
            <div class="mt-2">
              <InputGroup>
                <InputGroupAddon class="text-sm">https://www.</InputGroupAddon>
                <InputText placeholder="Website" class="input-style" v-model="form.website" />
              </InputGroup>
              <div class="text-red-500" v-if="form.errors.website">{{ form.errors.website }}</div>
            </div>
          </div>

          <div class="col-span-full">
            <label for="about" class="block text-sm font-medium leading-6 text-gray-900">Dealer informatie</label>
            <div class="mt-2">
              <textarea id="about" name="about" rows="3" v-model="form.description"
                class="input-style" />
            </div>
            <p class="mt-3 text-sm leading-6 text-gray-600">Korte uitleg over deze dealer.</p>
          </div>


          <div class="sm:col-span-3">
            <label for="chamber_of_commerce_number" class="block text-sm font-medium leading-6 text-gray-900">KvK
              nummer</label>
            <div class="mt-2">
              <div
                class="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-brightorange">
                <input type="text" name="chamber_of_commerce_number" id="chamber_of_commerce_number"
                  autocomplete="chamber_of_commerce_number" v-model="form.chamber_of_commerce_number"
                  class="input-style"
                  placeholder="KvK nummer" />
              </div>

              <div class="text-red-500" v-if="form.errors.chamber_of_commerce_number">{{
                form.errors.chamber_of_commerce_number }}</div>
            </div>
          </div>

          <div class="sm:col-span-3">
            <label for="vat_number" class="block text-sm font-medium leading-6 text-gray-900">BTW nummer</label>
            <div class="mt-2">
              <div
                class="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-brightorange">
                <input type="text" name="vat_number" id="vat_number" autocomplete="vat_number" v-model="form.vat_number"
                  class="input-style"
                  placeholder="BTW nummer" />
              </div>

              <div class="text-red-500" v-if="form.errors.vat_number">{{ form.errors.vat_number }}</div>
            </div>
          </div>

          <div class="col-span-full">
            <label for="username" class="block text-sm font-medium leading-6 text-gray-900">Adres</label>
            <div class="mt-2">

              <GoogleAutoComplete country="nl" name="address" id="address" ref="address"
                classname="input-style"
                placeholder="Begin met typen..." :country="['nl']" v-on:placechanged="getAndFormatAddressData">
              </GoogleAutoComplete>
              <div class="text-red-500" v-if="form.errors.address">{{ form.errors.address }}</div>
            </div>
          </div>

          <div class="col-span-full">
            <label for="username" class="block text-sm font-medium leading-6 text-gray-900">Factuuradres</label>
            <div class="mt-2">

              <GoogleAutoComplete country="nl" name="invoice_address" id="invoiceAddress" ref="invoiceAddress"
                classname="input-style"
                placeholder="Begin met typen..." :country="['nl']" v-on:placechanged="getAndFormatInvoiceAddressData">
              </GoogleAutoComplete>
              <div class="text-red-500" v-if="form.errors.invoice_address">{{ form.errors.invoice_address }}</div>
            </div>
          </div>

        </div>

      </div>
      <div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Contact informatie</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Vul hier alle informatie in om je zoekopdracht zo uitgebreid
            mogelijk te maken!</p>
        </div>
        <div class="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
          <div class="sm:col-span-2">
            <label for="first_name" class="block text-sm font-medium leading-6 text-gray-900">Voornaam
              contactpersoon</label>
            <div class="mt-2">
              <input type="text" name="first_name" id="first_name" autocomplete="first_name" v-model="form.first_name"
                class="input-style" />
            </div>
            <div class="text-red-500" v-if="form.errors.first_name">{{ form.errors.first_name }}</div>
          </div>
          <div>
            <label for="infix" class="block text-sm font-medium leading-6 text-gray-900">Tussenvoegsel</label>
            <div class="mt-2">
              <input type="text" name="infix" id="infix" v-model="form.infix"
                class="input-style" />
            </div>
            <div class="text-red-500" v-if="form.errors.infix">{{ form.errors.infix }}</div>
          </div>
          <div class="sm:col-span-3">
            <label for="last_name" class="block text-sm font-medium leading-6 text-gray-900">Achternaam
              contactpersoon</label>
            <div class="mt-2">
              <input type="text" name="last_name" id="last_name" v-model="form.last_name"
                class="input-style" />
            </div>
            <div class="text-red-500" v-if="form.errors.last_name">{{ form.errors.last_name }}</div>
          </div>
          <div class="sm:col-span-3">
            <label for="phone_number" class="block text-sm font-medium leading-6 text-gray-900">Telefoonnummer (Whatsapp / 06)</label>
            <div class="mt-2">
              <input type="text" name="phone_number" id="phone_number" v-model="form.whatsapp_number"
                class="input-style" />
            </div>
            <div class="text-red-500" v-if="form.errors.whatsapp_number">{{ form.errors.whatsapp_number }}</div>
          </div>
          <div class="sm:col-span-3">
            <label for="phone_number" class="block text-sm font-medium leading-6 text-gray-900">Telefoonnummer (vaste lijn)</label>
            <div class="mt-2">
              <input type="text" name="phone_number" id="phone_number" v-model="form.phone"
                class="input-style" />
            </div>
            <div class="text-red-500" v-if="form.errors.phone">{{ form.errors.phone }}</div>
          </div>

        </div>
      </div>
      <div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Voorkeuren</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Een gepersonaliseerd aanbod werkt wel zo gemakkelijk. Geef aan
            welke merken je graag wilt zien.</p>
        </div>
        <div class="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
          <div class="sm:col-span-6">
            <label for="first-name" class="block text-sm font-medium leading-6 text-gray-900">Favoriete merken</label>
            <div class="mt-2 flex flex-wrap gap-x-2 gap-y-6">
              <div v-for="brand in props.brands" :key="brand.id">
                <Checkbox v-model="form.brands" :value="brand.id" :inputId="'brand_' + brand.id" class="hidden" />
                <label :for="'brand_' + brand.id"
                  :class="form.brands.includes(brand.id) ? 'bg-royalblue-500 text-white' : 'bg-transparent'"
                  class="border p-2 rounded-lg hover:bg-royalblue-500/80 text-gray-900 hover:text-white cursor-pointer transition-colors duration-200 ease-in-out">{{
                  brand.name }}</label>
              </div>
            </div>
          </div>
          <div class="sm:col-span-6">
            <label for="first-name" class="block text-sm font-medium leading-6 text-gray-900">Categorieën</label>
            <div class="mt-2 flex flex-wrap gap-x-2 gap-y-6">
              <div v-for="category in props.categories" :key="category.id">
                <Checkbox v-model="form.categories" :value="category.id" :inputId="'category_' + category.id"
                  class="hidden" />
                <label :for="'category_' + category.id"
                  :class="form.categories.includes(category.id) ? 'bg-royalblue-500 text-white' : 'bg-transparent'"
                  class="border p-2 rounded-lg hover:bg-royalblue-500/80 text-gray-900 hover:text-white cursor-pointer transition-colors duration-200 ease-in-out">{{
                  category.name }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
        <div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Notificaties</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Graag horen wanneer er nieuwe en interessant aanbod
            is?</p>
        </div>
        <div class="space-y-10 md:col-span-3">
          <div class="grid grid-cols-1 gap-x-6 gap-y-8 md:grid-cols-3">
            <fieldset>
              <legend class="text-sm font-semibold leading-6 text-gray-900">Emails</legend>
              <div class="mt-6 space-y-2">
                <div class="relative flex gap-x-3">
                  <Switch v-model="form.notifications.email.new_offer"
                    class="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-brightorange focus:ring-offset-2">
                    <span class="sr-only">Use setting</span>
                    <span aria-hidden="true" class="pointer-events-none absolute h-full w-full rounded-md bg-white" />
                    <span aria-hidden="true"
                      :class="[form.notifications.email.new_offer ? 'bg-royalblue-500' : 'bg-gray-200', 'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out']" />
                    <span aria-hidden="true"
                      :class="[form.notifications.email.new_offer ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out']" />
                  </Switch>
                  <div class="text-sm leading-6">
                    <label for="comments" class="font-medium text-gray-900">Nieuw/interessant aanbod</label>
                  </div>
                </div>
                <div class="relative flex gap-x-3">
                  <Switch v-model="form.notifications.email.personal_offer"
                    class="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-brightorange focus:ring-offset-2">
                    <span class="sr-only">Use setting</span>
                    <span aria-hidden="true" class="pointer-events-none absolute h-full w-full rounded-md bg-white" />
                    <span aria-hidden="true"
                      :class="[form.notifications.email.personal_offer ? 'bg-royalblue-500' : 'bg-gray-200', 'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out']" />
                    <span aria-hidden="true"
                      :class="[form.notifications.email.personal_offer ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out']" />
                  </Switch>
                  <div class="text-sm leading-6">
                    <label for="comments" class="font-medium text-gray-900">Persoonlijke aanbiedingen</label>
                  </div>
                </div>
              </div>
            </fieldset>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-6 flex items-center justify-end gap-x-6">
      <Link href="/portal/dealers" type="button" class="text-sm font-semibold leading-6 text-gray-900">Annuleren</Link>
      <button type="submit"
        class="rounded-md bg-brightorange px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brightorange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brightorange">Opslaan</button>
    </div>
  </form>
</template>
